import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { Avatar, Fade, IconButton, Snackbar } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MUIStyled } from "../shared/MUIStyled";
import SABox from "../shared/SABox";
import SAStack from "../shared/SAStack";
import SATruncateWithEllipsis from "../shared/SATruncateWithEllipsis";
import SATypography from "../shared/SATypography";
import { decodeToken, getNotifications } from "../utils/functions";

const NotificationWrapper = MUIStyled(SABox)({
  background: "#fdfdfd",
  border: "1px solid #e0dacb",
  borderColor: "grey.300",
  borderRadius: 12,
  boxShadow: "0px 8px 20px #78725e33",
  paddingTop: 16,
  paddingBottom: 16,
  paddingLeft: 20,
  paddingRight: 20,
  minWidth: 340,
  maxWidth: 400,
  display: "flex",
  flexDirection: "column",
  gap: 4,
});

const { role } = decodeToken(localStorage.getItem("accessToken"));

export const notificationModuleMap = {
  PURCHASE_REQUEST_CREATED: "purchase-requests",
  PURCHASE_REQUEST_APPROVED:
    role === "TAQABETH_ENFORCER" ? "request-management" : "purchase-requests",
  PURCHASE_REQUEST_REJECTED: "purchase-requests",
  SALE_REQUEST_APPROVED:
    role === "TAQABETH_ENFORCER" ? "request-management" : "purchase-requests",
  SALE_REQUEST_CREATED: "purchase-requests",
  SALE_REQUEST_REJECTED: "purchase-requests",
  SALE_REQUEST_COMPLETED: "purchase-requests",
  PURCHASE_REQUEST_PAYMENT_TRANSFER: "bank-transfer-requests",
  PURCHASE_REQUEST_PAYMENT_RECEIVED: "bank-transfer-requests",
  PURCHASE_REQUEST_PAYMENT_FAILED: "bank-transfer-requests",
  WITHDRAW_REQUEST_APPROVED: "bank-transfer-requests",
  WITHDRAW_REQUEST_REJECTED: "bank-transfer-requests",
  WITHDRAW_REQUEST_CREATED: "bank-transfer-requests",
  DEPOSIT_REQUEST_APPROVED: "bank-transfer-requests",
  DEPOSIT_REQUEST_REJECTED: "bank-transfer-requests",
  DEPOSIT_REQUEST_CREATED: "bank-transfer-requests",
  MUSHARAKAH_CONTRACT_TERMINATION_REQUEST: "musharka-contracts",
  MUSHARAKAH_CONTRACT_REQUEST_CREATED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_RENEWAL: "musharka-contracts",
  MUSHARAKAH_CONTRACT_TERMINATED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_TERMINATION_REQUEST_APPROVED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_TERMINATION_REQUEST_REJECTED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_REQUEST_APPROVED:
    role === "TAQABETH_ENFORCER"
      ? "request-management/musharakah"
      : "musharka-contracts",
  MUSHARAKAH_CONTRACT_REQUEST_REJECTED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_REQUESTS_CLOSED: "musharka-contracts",
  MUSHARAKAH_CONTRACT_TERMINATION_PAYMENT: "musharka-contracts",
  MUSHARAKAH_CONTRACT_TERMINATION_PAYMENT_REQUEST: "musharka-contracts",
  NO_INVESTOR_ASSIGNED_TO_MUSHARAKAH_CONTRACT_REQUEST: "musharka-contracts",
  ASSET_CONTRIBUTED_IN_MUSHARAKAH_CONTRACT_REQUEST: "musharka-contracts",
  ASSET_CONTRIBUTED_IN_POOL:
    role === "TAQABETH_ENFORCER"
      ? "request-management/pool"
      : "metals-stones-pools",
  POOL_CONTRIBUTION_APPROVED:
    role === "TAQABETH_ENFORCER"
      ? "request-management/pool"
      : "metals-stones-pools",
  POOL_CONTRIBUTION_REJECTED: "metals-stones-pools",
  POOL_CLOSED_DATE_UPDATED:
    role === "TAQABETH_ENFORCER"
      ? "request-management/pool"
      : "metals-stones-pools",
  MANUFACTURING_REQUEST_CREATED: "manufacturer",
  MANUFACTURING_ESTIMATION_REQUEST: "manufacturer",
  MANUFACTURING_ESTIMATION_REQUEST_ACCEPTED: "manufacturer",
  MANUFACTURING_ESTIMATION_REQUEST_REJECTED: "manufacturer",
  MANUFACTURING_REQUEST_PAYMENT: "bank-transfer-requests",
  JEWELRY_PRODUCTION_STATUS_UPDATED:
    role === "JEWELLERY_INSPECTOR"
      ? "inspection-requests"
      : "manufacturer/processing-details",
  JEWELRY_PRODUCT_INSPECTION:
    role === "JEWELLERY_INSPECTOR"
      ? "inspection-requests"
      : "manufacturer/processing-details",
  JEWELRY_PRODUCTION_PAYMENT: "bank-transfer-requests",
  JEWELRY_PRODUCTION_DELIVERY_STATUS:
    role === "JEWELLERY_INSPECTOR"
      ? "inspection-requests"
      : "manufacturer/processing-details",
};

const MODULE_META_MAP = {
  "request-management/musharakah": {
    homeRoute: "/requests-management",
    label: "request-management",
    href: "/request-management?tab=taqabeth-requests",
  },
  "request-management/pool": {
    homeRoute: "/requests-management",
    label: "request-management",
    href: "/request-management?tab=taqabeth-requests",
  },
};

const STATE_MODULES = [
  "request-management/musharakah",
  "request-management/pool",
];

const Notifications = () => {
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [showNotification, setShowNotification] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    const { role: currentRole } = decodeToken(token);

    const notificationUrl = `${process.env.REACT_APP_WEB_SOCKET_URL}notifications/?token=${token}`;
    const socket = new WebSocket(notificationUrl);
    socket.onopen = () => {};

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      // Check if notification is for this role
      if (!data?.data) return;
      if (!data.data?.role || data.data.role === currentRole) {
        setNotification(data);
        setShowNotification(true);
        getNotifications();
      }
      // setNotification(JSON.parse(event.data));
      // setShowNotification(true);
      // getNotifications();
    };

    socket.onerror = (error) => {
      // eslint-disable-next-line no-console
      console.error("WebSocket error:", error);
    };

    return () => socket.close();
  }, []);

  const id = notification?.data?.object_id;
  const type = notification?.data?.notification_type;

  const buildNavigateState = (notificationType) => {
    const moduleKey = notificationModuleMap[notificationType];
    if (!STATE_MODULES.includes(moduleKey)) return null;
    const meta = MODULE_META_MAP[moduleKey] || {};
    return {
      homeRoute: meta.homeRoute || moduleKey || "",
      label: meta.label || moduleKey || "",
      href: meta.href || (moduleKey ? `${moduleKey}` : ""),
    };
  };

  const handleNotificationClick = ({ id, type }) => {
    const currentRoute = notificationModuleMap[type];

    if (!currentRoute) return;

    const navState = buildNavigateState(type);
    if (navState) {
      navigate(`/${currentRoute}/${id}`, { state: navState });
    } else {
      navigate(`/${currentRoute}/${id}`);
    }
  };

  const handleClose = () => setShowNotification(false);

  if (!notification) return <></>;

  return (
    <Snackbar
      onClose={handleClose}
      open={showNotification}
      autoHideDuration={5000}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Fade}
    >
      <div
        style={{ cursor: "pointer" }}
        onClick={() =>
          handleNotificationClick({
            id: id,
            type: type,
          })
        }
      >
        <NotificationWrapper>
          <SABox
            display="flex"
            justifyContent="space-between"
            alignItems="start"
          >
            <SAStack gap={0.5}>
              <SAStack direction="row" alignItems="center" gap={1}>
                <Avatar
                  variant="circular"
                  sx={{ bgcolor: "#dfe9f8", width: 40, height: 40 }}
                >
                  <NotificationsActiveOutlinedIcon
                    sx={{ color: "primary.main" }}
                  />
                </Avatar>
                <SATypography
                  fontWeight="bold"
                  variant="subtitle1"
                  color="text.primary"
                >
                  {notification?.data?.title}
                </SATypography>
              </SAStack>
              <SATypography
                mt={0.5}
                variant="body2"
                color="gray.light300"
                lineHeight={1.5}
                fontWeight={500}
              >
                <SATruncateWithEllipsis
                  text={notification?.data?.body}
                  maxLength={80}
                />
              </SATypography>
            </SAStack>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              size="small"
              sx={{
                ml: 1,
                mt: "-4px",
                color: "#24344e",
                "&:hover": { color: "#374151" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </SABox>
        </NotificationWrapper>
      </div>
    </Snackbar>
  );
};

export default Notifications;
